import { useCallback, useEffect, useRef, useState } from 'react';
import { getFileFormat, getFileNameFromUrl, isAndroid, isFirefox, isVideo } from '../../Utils';

const FALLBACK_MS = 3000;

export function isInfluencerAvatarVideo(url) {
    return Boolean(url) && isVideo(getFileFormat(getFileNameFromUrl(url)));
}

function shouldForceImageAvatarFallback() {
    // Firefox Android : vidéo → canvas souvent sans frame utilisable.
    return isFirefox() && isAndroid();
}

function preloadImage(url, onDone) {
    if (!url) {
        onDone();
        return () => {};
    }
    let cancelled = false;
    const img = new Image();
    const finalize = () => {
        if (!cancelled) {
            onDone();
        }
    };
    img.onload = finalize;
    img.onerror = finalize;
    img.src = url;
    return () => {
        cancelled = true;
    };
}

export function useInfluencerAvatarMedia({ enabled, videoUrl, imageUrl }) {
    const videoRef = useRef(null);
    const [contentReady, setContentReady] = useState(false);
    const [useImageFallback, setUseImageFallback] = useState(false);
    const sessionRef = useRef({
        videoWon: false,
        fallbackCommitted: false,
        timeoutId: null,
        cancelImagePreload: null,
    });

    const hasVideoAvatar = isInfluencerAvatarVideo(videoUrl);
    const forceImageFallback = shouldForceImageAvatarFallback();
    const showVideoAvatar = hasVideoAvatar && !useImageFallback && !forceImageFallback;

    const clearTimers = useCallback(() => {
        const session = sessionRef.current;
        if (session.timeoutId != null) {
            window.clearTimeout(session.timeoutId);
            session.timeoutId = null;
        }
        if (session.cancelImagePreload) {
            session.cancelImagePreload();
            session.cancelImagePreload = null;
        }
    }, []);

    const resetSession = useCallback(() => {
        sessionRef.current.videoWon = false;
        sessionRef.current.fallbackCommitted = false;
        clearTimers();
        setContentReady(false);
        setUseImageFallback(false);
    }, [clearTimers]);

    const revealContent = useCallback(() => {
        setContentReady(true);
    }, []);

    const commitImageFallback = useCallback(() => {
        const session = sessionRef.current;
        if (session.fallbackCommitted) {
            return;
        }
        if (session.videoWon) {
            return;
        }
        session.fallbackCommitted = true;
        clearTimers();
        setUseImageFallback(true);
        session.cancelImagePreload = preloadImage(imageUrl, () => {
            session.cancelImagePreload = null;
            revealContent();
        });
    }, [imageUrl, clearTimers, revealContent]);

    const onVideoReady = useCallback(() => {
        const session = sessionRef.current;
        if (session.fallbackCommitted || forceImageFallback) {
            return;
        }
        clearTimers();
        session.videoWon = true;
        setUseImageFallback(false);
        revealContent();
    }, [clearTimers, revealContent, forceImageFallback]);

    const onVideoError = useCallback(() => {
        commitImageFallback();
    }, [commitImageFallback]);

    useEffect(() => {
        if (!enabled) {
            resetSession();
            return undefined;
        }

        if (!hasVideoAvatar) {
            setUseImageFallback(false);
            revealContent();
            return undefined;
        }

        const session = sessionRef.current;
        session.videoWon = false;
        session.fallbackCommitted = false;
        clearTimers();
        setContentReady(false);

        // Firefox Android : passer directement sur l'image (pas de canvas vidéo).
        if (forceImageFallback) {
            setUseImageFallback(true);
            session.fallbackCommitted = true;
            session.cancelImagePreload = preloadImage(imageUrl, () => {
                session.cancelImagePreload = null;
                revealContent();
            });
            return clearTimers;
        }

        setUseImageFallback(false);

        session.timeoutId = window.setTimeout(() => {
            session.timeoutId = null;
            if (session.videoWon || session.fallbackCommitted) {
                return;
            }
            if (imageUrl) {
                commitImageFallback();
                return;
            }
            session.videoWon = true;
            revealContent();
        }, FALLBACK_MS);

        return clearTimers;
    }, [
        enabled,
        hasVideoAvatar,
        forceImageFallback,
        videoUrl,
        imageUrl,
        clearTimers,
        commitImageFallback,
        revealContent,
        resetSession,
    ]);

    return {
        videoRef,
        hasVideoAvatar,
        showVideoAvatar,
        contentReady,
        fadeAnimationKey: (useImageFallback || forceImageFallback) ? 'influencer-image-fallback' : 'influencer-video',
        onVideoReady,
        onVideoError,
    };
}
